import React, { useState, useEffect } from 'react';
import AdminLayout from './AdminLayout';
import { supabase } from './supabaseClient';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from 'recharts';
import './Reports.css';

const Reports = () => {
  const [chartPeriod, setChartPeriod] = useState('30');
  const [loading, setLoading] = useState(true);
  
  // Real metrics data
  const [metrics, setMetrics] = useState({
    totalBookings: 0,
    totalRevenue: '₦0.00',
    activeUsers: 0,
    pendingRefunds: 0
  });

  // Real bookings data for chart
  const [bookingsData, setBookingsData] = useState({
    '7': [],
    '30': [],
    '90': []
  });

  // Real revenue by route data
  const [revenueByRoute, setRevenueByRoute] = useState([]);
  const [paymentStatusData, setPaymentStatusData] = useState([]);
  const [revenueByRoutePieData, setRevenueByRoutePieData] = useState([]);

  // Fetch real data from Supabase
  useEffect(() => {
    const fetchReportsData = async () => {
      try {
        setLoading(true);

        // 1. Fetch total bookings count
        const { count: totalBookingsCount } = await supabase
          .from('flight_bookings')
          .select('*', { count: 'exact', head: true });

        // 2. Fetch total revenue from completed payments
        const { data: completedPayments } = await supabase
          .from('booking_payments')
          .select('amount')
          .eq('payment_status', 'completed');

        const totalRevenue = completedPayments?.reduce((sum, p) => sum + parseFloat(p.amount || 0), 0) || 0;

        // 3. Fetch active users count
        const { count: activeUsersCount } = await supabase
          .from('users')
          .select('*', { count: 'exact', head: true })
          .eq('status', 'active');

        // 4. Fetch pending refunds
        const { count: refundsCount } = await supabase
          .from('booking_payments')
          .select('*', { count: 'exact', head: true })
          .eq('payment_status', 'refunded');

        // Update metrics
        setMetrics({
          totalBookings: totalBookingsCount || 0,
          totalRevenue: totalRevenue >= 1000000 
            ? `₦${(totalRevenue / 1000000).toFixed(1)}M` 
            : `₦${totalRevenue.toLocaleString('en-NG', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
          activeUsers: activeUsersCount || 0,
          pendingRefunds: refundsCount || 0
        });

        // 5. Fetch bookings for charts (7, 30, 90 days)
        const now = new Date();
        const periods = { '7': 7, '30': 30, '90': 90 };
        const chartData = {};

        for (const [period, days] of Object.entries(periods)) {
          const startDate = new Date(now);
          startDate.setDate(startDate.getDate() - days);

          const { data: bookingsForPeriod } = await supabase
            .from('flight_bookings')
            .select('booking_date')
            .gte('booking_date', startDate.toISOString())
            .order('booking_date', { ascending: true });

          // Group bookings by date
          const bookingsByDate = {};
          bookingsForPeriod?.forEach(booking => {
            const date = new Date(booking.booking_date).toLocaleDateString('en-CA');
            bookingsByDate[date] = (bookingsByDate[date] || 0) + 1;
          });

          // Create array for chart
          const periodData = [];
          for (let i = days - 1; i >= 0; i--) {
            const date = new Date(now);
            date.setDate(date.getDate() - i);
            const dateStr = date.toLocaleDateString('en-CA');
            periodData.push(bookingsByDate[dateStr] || 0);
          }
          chartData[period] = periodData;
        }
        setBookingsData(chartData);

        // 6. Fetch revenue by route
        const { data: bookingsWithRoutes } = await supabase
          .from('flight_bookings')
          .select('outbound_departure_city, outbound_arrival_city, outbound_departure_airport, outbound_arrival_airport, total_amount, payment_status')
          .eq('payment_status', 'paid')
          .limit(1000);

        const routeRevenue = {};
        bookingsWithRoutes?.forEach(booking => {
          const route = booking.outbound_departure_city && booking.outbound_arrival_city
            ? `${booking.outbound_departure_city} → ${booking.outbound_arrival_city}`
            : booking.outbound_departure_airport && booking.outbound_arrival_airport
            ? `${booking.outbound_departure_airport} → ${booking.outbound_arrival_airport}`
            : 'Unknown Route';
          const amount = parseFloat(booking.total_amount || 0);
          if (amount > 0) {
            routeRevenue[route] = (routeRevenue[route] || 0) + amount;
          }
        });

        const sortedRoutes = Object.entries(routeRevenue)
          .sort((a, b) => b[1] - a[1])
          .slice(0, 5);
        
        const totalRouteRevenue = sortedRoutes.reduce((sum, [, amount]) => sum + amount, 0);
        const routeData = sortedRoutes.map(([route, revenue]) => ({
          route,
          revenue,
          percentage: totalRouteRevenue > 0 ? Math.round((revenue / totalRouteRevenue) * 100 * 10) / 10 : 0
        }));
        setRevenueByRoute(routeData);

        // Create pie chart data for revenue by route
        const routePieData = sortedRoutes.length > 0 ? sortedRoutes.map(([route, revenue]) => ({
          name: route.length > 20 ? route.substring(0, 20) + '...' : route,
          value: revenue
        })) : [];
        setRevenueByRoutePieData(routePieData);

        // 7. Fetch payment status data for pie chart
        const { data: allPayments, error: paymentsError } = await supabase
          .from('booking_payments')
          .select('payment_status');

        if (paymentsError) {
          console.error('Error fetching payment status data:', paymentsError);
        }

        const paymentStatusCounts = {};
        if (allPayments && allPayments.length > 0) {
          allPayments.forEach(payment => {
            const status = payment.payment_status || 'unknown';
            paymentStatusCounts[status] = (paymentStatusCounts[status] || 0) + 1;
          });
        }

        const paymentStatusChartData = Object.entries(paymentStatusCounts).map(([name, value]) => ({
          name: name.charAt(0).toUpperCase() + name.slice(1),
          value
        }));

        setPaymentStatusData(paymentStatusChartData);

      } catch (err) {
        console.error('Error fetching reports data:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchReportsData();
  }, [chartPeriod]);


  const maxBookings = bookingsData[chartPeriod] && bookingsData[chartPeriod].length > 0 
    ? Math.max(...bookingsData[chartPeriod]) 
    : 1;
  const maxRevenue = revenueByRoute.length > 0 
    ? Math.max(...revenueByRoute.map(r => r.revenue)) 
    : 1;

  // Colors for pie charts
  const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#06b6d4'];
  
  const RADIAN = Math.PI / 180;
  const renderCustomizedLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent }) => {
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);

    return (
      <text x={x} y={y} fill="white" textAnchor={x > cx ? 'start' : 'end'} dominantBaseline="central" fontSize={12} fontWeight="bold">
        {`${(percent * 100).toFixed(0)}%`}
      </text>
    );
  };

  const handleExport = (format) => {
    console.log('Export reports as:', format);
  };

  return (
    <AdminLayout pageTitle="Reports">
      <div className="admin-reports-page">
        {/* Page Title */}
        <div className="admin-reports-header">
          <h1 className="admin-reports-title">Reports</h1>
          <p className="admin-reports-subtitle">View and export platform analytics</p>
        </div>

        {/* Loading State */}
        {loading && (
          <div style={{ 
            textAlign: 'center', 
            padding: '40px',
            color: '#666'
          }}>
            <div style={{ fontSize: '24px', marginBottom: '10px' }}>⏳</div>
            <div>Loading reports data...</div>
          </div>
        )}

        {/* Top Metrics Cards */}
        {!loading && (
        <div className="admin-reports-metrics">
          <div className="admin-metric-card">
            <div className="admin-metric-icon">🎫</div>
            <div className="admin-metric-content">
              <div className="admin-metric-label">Total Bookings</div>
              <div className="admin-metric-value">{metrics.totalBookings.toLocaleString()}</div>
            </div>
          </div>
          <div className="admin-metric-card">
            <div className="admin-metric-icon">💰</div>
            <div className="admin-metric-content">
              <div className="admin-metric-label">Total Revenue</div>
              <div className="admin-metric-value">{metrics.totalRevenue}</div>
            </div>
          </div>
          <div className="admin-metric-card">
            <div className="admin-metric-icon">👥</div>
            <div className="admin-metric-content">
              <div className="admin-metric-label">Active Users</div>
              <div className="admin-metric-value">{metrics.activeUsers.toLocaleString()}</div>
            </div>
          </div>
          <div className="admin-metric-card">
            <div className="admin-metric-icon">↩️</div>
            <div className="admin-metric-content">
              <div className="admin-metric-label">Pending Refunds</div>
              <div className="admin-metric-value">{metrics.pendingRefunds}</div>
            </div>
          </div>
        </div>
        )}

        {/* Charts Section */}
        {!loading && (
        <div className="admin-reports-charts">
          {/* Bookings Over Time Chart */}
          <div className="admin-chart-card">
            <div className="admin-chart-header">
              <h3 className="admin-chart-title">Bookings Over Time</h3>
              <div className="admin-chart-controls">
                <button
                  className={`admin-chart-period-button ${chartPeriod === '7' ? 'active' : ''}`}
                  onClick={() => setChartPeriod('7')}
                >
                  7 Days
                </button>
                <button
                  className={`admin-chart-period-button ${chartPeriod === '30' ? 'active' : ''}`}
                  onClick={() => setChartPeriod('30')}
                >
                  30 Days
                </button>
                <button
                  className={`admin-chart-period-button ${chartPeriod === '90' ? 'active' : ''}`}
                  onClick={() => setChartPeriod('90')}
                >
                  90 Days
                </button>
              </div>
            </div>
            <div className="admin-chart-content">
              {bookingsData[chartPeriod] && bookingsData[chartPeriod].length > 0 ? (
                <div className="admin-line-chart">
                  {bookingsData[chartPeriod].map((value, index) => {
                    const height = (value / maxBookings) * 100;
                    return (
                      <div key={index} className="admin-chart-bar">
                        <div
                          className="admin-chart-bar-fill"
                          style={{ height: `${height}%` }}
                          title={`${value} bookings`}
                        />
                        <div className="admin-chart-bar-label">
                          {chartPeriod === '7' ? `Day ${index + 1}` : index + 1}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
                  No booking data available for this period
                </div>
              )}
            </div>
          </div>

          {/* Revenue by Route Chart */}
          <div className="admin-chart-card">
            <div className="admin-chart-header">
              <h3 className="admin-chart-title">Revenue by Route</h3>
            </div>
            <div className="admin-chart-content">
              {revenueByRoute.length > 0 ? (
                <div className="admin-revenue-chart">
                  {revenueByRoute.map((route, index) => {
                    const width = (route.revenue / maxRevenue) * 100;
                    return (
                      <div key={index} className="admin-revenue-item">
                        <div className="admin-revenue-label-row">
                          <span className="admin-revenue-route">{route.route}</span>
                          <span className="admin-revenue-amount">
                            {route.revenue >= 1000000 
                              ? `₦${(route.revenue / 1000000).toFixed(1)}M` 
                              : `₦${route.revenue.toLocaleString('en-NG', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                          </span>
                        </div>
                        <div className="admin-revenue-bar-wrapper">
                          <div
                            className="admin-revenue-bar"
                            style={{ width: `${width}%` }}
                          />
                          <span className="admin-revenue-percentage">{route.percentage}%</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
                  No revenue data available
                </div>
              )}
            </div>
          </div>

          {/* Payment Status Pie Chart */}
          <div className="admin-chart-card">
            <div className="admin-chart-header">
              <h3 className="admin-chart-title">Payment Status Distribution</h3>
            </div>
            <div className="admin-chart-content">
              {paymentStatusData.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={paymentStatusData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={renderCustomizedLabel}
                      outerRadius={100}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {paymentStatusData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
                  No payment data available
        </div>
        )}
            </div>
          </div>

          {/* Revenue by Route Pie Chart */}
          <div className="admin-chart-card">
            <div className="admin-chart-header">
              <h3 className="admin-chart-title">Revenue by Route (Pie Chart)</h3>
            </div>
            <div className="admin-chart-content">
              {revenueByRoutePieData.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={revenueByRoutePieData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={renderCustomizedLabel}
                      outerRadius={100}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {revenueByRoutePieData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value) => `₦${value.toLocaleString('en-NG', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`} />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
                  No revenue data available
                </div>
              )}
            </div>
          </div>
        </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default Reports;

